import { ChatOpenAI } from '@langchain/openai';
import { ChatPromptTemplate } from '@langchain/core/prompts';
import { SportShot } from '../content';

export interface CoachingRequest {
  sport: string;
  shot: SportShot;
  issue: string;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  weakAreas: string[];
  previousRecommendations?: string[];
}

export interface CoachingResponse {
  recommendations: string[];
  explanation: string;
  priorityAreas: string[];
  practiceTips: string[];
}

class AICoachingService {
  private llm: ChatOpenAI | null = null;
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      console.warn('OpenAI API key not found. AI coaching will use fallback recommendations.');
      this.isInitialized = false;
      return;
    }

    try {
      this.llm = new ChatOpenAI({
        modelName: 'gpt-4o-mini', // Using cost-effective model
        temperature: 0.7,
        maxTokens: 500,
        openAIApiKey: apiKey,
      });
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize AI coaching service:', error);
      this.isInitialized = false;
    }
  }

  private createCoachingPrompt(): ChatPromptTemplate {
    return ChatPromptTemplate.fromMessages([
      [
        'system',
        `You are an expert sports coach specializing in {sport}. Your role is to provide personalized, actionable coaching recommendations to help players improve their technique.

Guidelines:
- Provide specific, actionable advice tailored to the player's skill level
- Focus on the most critical issues first
- Use clear, concise language
- Reference proper technique fundamentals
- Consider the player's weak areas when prioritizing recommendations
- Provide 3-5 key recommendations maximum
- Include practice tips that are appropriate for the skill level
- Be encouraging and constructive

Format your response as JSON with this structure:
{{
  "recommendations": ["rec1", "rec2", "rec3"],
  "explanation": "Brief explanation of why these recommendations matter",
  "priorityAreas": ["area1", "area2"],
  "practiceTips": ["tip1", "tip2"]
}}`,
      ],
      [
        'human',
        `Sport: {sport}
Shot Type: {shotName}
Shot Description: {shotDescription}
Issue: {issue}
Player Skill Level: {skillLevel}
Weak Areas: {weakAreas}
Common Issues for this Shot: {commonIssues}
Standard Improvements: {standardImprovements}

Please provide personalized coaching recommendations for this player.`,
      ],
    ]);
  }

  async getCoachingRecommendations(
    request: CoachingRequest
  ): Promise<CoachingResponse> {
    // Fallback to static recommendations if AI is not available
    if (!this.isInitialized || !this.llm) {
      return this.getFallbackRecommendations(request);
    }

    try {
      const prompt = this.createCoachingPrompt();
      
      const chain = prompt.pipe(this.llm);
      
      const response = await chain.invoke({
        sport: request.sport,
        shotName: request.shot.name,
        shotDescription: request.shot.description,
        issue: request.issue,
        skillLevel: request.skillLevel,
        weakAreas: request.weakAreas.join(', ') || 'None identified',
        commonIssues: request.shot.commonIssues.join(', '),
        standardImprovements: request.shot.improvements.join(', '),
      });

      // Parse the response
      const content = response.content as string;
      
      // Try to extract JSON from the response
      let parsedResponse: CoachingResponse;
      try {
        // Look for JSON in the response
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedResponse = JSON.parse(jsonMatch[0]);
        } else {
          // If no JSON found, create response from text
          parsedResponse = this.parseTextResponse(content, request);
        }
      } catch (parseError) {
        console.warn('Failed to parse AI response as JSON, using text parsing:', parseError);
        parsedResponse = this.parseTextResponse(content, request);
      }

      // Validate and enhance response
      return this.validateResponse(parsedResponse, request);
    } catch (error) {
      console.error('Error getting AI coaching recommendations:', error);
      return this.getFallbackRecommendations(request);
    }
  }

  private parseTextResponse(
    text: string,
    request: CoachingRequest
  ): CoachingResponse {
    // Extract recommendations from text
    const recommendations: string[] = [];
    const practiceTips: string[] = [];
    
    // Look for numbered or bulleted lists
    const lines = text.split('\n');
    let inRecommendations = false;
    let inTips = false;

    for (const line of lines) {
      const trimmed = line.trim();
      
      // Detect recommendation sections
      if (trimmed.toLowerCase().includes('recommendation') || 
          trimmed.toLowerCase().includes('improve')) {
        inRecommendations = true;
        inTips = false;
        continue;
      }
      
      if (trimmed.toLowerCase().includes('practice') || 
          trimmed.toLowerCase().includes('tip')) {
        inTips = true;
        inRecommendations = false;
        continue;
      }

      // Extract list items
      if (trimmed.match(/^[-•*]\s/) || trimmed.match(/^\d+[\.\)]\s/)) {
        const item = trimmed.replace(/^[-•*\d\.\)]\s+/, '').trim();
        if (item && item.length > 10) {
          if (inRecommendations && recommendations.length < 5) {
            recommendations.push(item);
          } else if (inTips && practiceTips.length < 3) {
            practiceTips.push(item);
          }
        }
      }
    }

    // Fallback: use first few sentences as recommendations
    if (recommendations.length === 0) {
      const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 20);
      recommendations.push(...sentences.slice(0, 5).map(s => s.trim()));
    }

    return {
      recommendations: recommendations.length > 0 
        ? recommendations 
        : request.shot.improvements.slice(0, 5),
      explanation: text.substring(0, 200) || 'Focus on the fundamentals and practice consistently.',
      priorityAreas: request.weakAreas.length > 0 
        ? request.weakAreas.slice(0, 2) 
        : [request.issue],
      practiceTips: practiceTips.length > 0 
        ? practiceTips 
        : ['Practice consistently', 'Focus on one improvement at a time'],
    };
  }

  private validateResponse(
    response: Partial<CoachingResponse>,
    request: CoachingRequest
  ): CoachingResponse {
    return {
      recommendations: Array.isArray(response.recommendations) && response.recommendations.length > 0
        ? response.recommendations.slice(0, 5)
        : request.shot.improvements.slice(0, 5),
      explanation: response.explanation || 'Focus on improving your technique through consistent practice.',
      priorityAreas: Array.isArray(response.priorityAreas) && response.priorityAreas.length > 0
        ? response.priorityAreas.slice(0, 3)
        : request.weakAreas.length > 0 
          ? request.weakAreas.slice(0, 2)
          : [request.issue],
      practiceTips: Array.isArray(response.practiceTips) && response.practiceTips.length > 0
        ? response.practiceTips.slice(0, 3)
        : ['Practice consistently', 'Focus on one improvement at a time', 'Record yourself to track progress'],
    };
  }

  private getFallbackRecommendations(request: CoachingRequest): CoachingResponse {
    // Smart fallback that still personalizes based on skill level
    let recommendations = [...request.shot.improvements];
    
    // Filter by skill level
    if (request.skillLevel === 'beginner') {
      recommendations = recommendations.filter(rec => 
        !rec.toLowerCase().includes('advanced') &&
        !rec.toLowerCase().includes('complex')
      );
      
      if (recommendations.length === 0) {
        recommendations = [
          'Focus on proper grip and stance first',
          'Practice the basic motion slowly',
          'Work on consistency before power',
          'Get comfortable with the fundamentals',
        ];
      }
    } else if (request.skillLevel === 'advanced') {
      recommendations = recommendations.filter(rec => 
        !rec.toLowerCase().includes('basic') &&
        !rec.toLowerCase().includes('start')
      );
    }

    // Prioritize weak areas
    const weakAreaRecs = recommendations.filter(rec => {
      return request.weakAreas.some(weakArea => 
        rec.toLowerCase().includes(weakArea.toLowerCase()) ||
        request.issue.toLowerCase().includes(weakArea.toLowerCase())
      );
    });

    if (weakAreaRecs.length > 0) {
      recommendations = [...weakAreaRecs, ...recommendations.filter(rec => !weakAreaRecs.includes(rec))];
    }

    return {
      recommendations: recommendations.slice(0, 5),
      explanation: `Based on your ${request.skillLevel} skill level and the issue "${request.issue}", focus on these key improvements.`,
      priorityAreas: request.weakAreas.length > 0 
        ? request.weakAreas.slice(0, 2) 
        : [request.issue],
      practiceTips: [
        'Practice consistently',
        'Focus on one improvement at a time',
        'Record yourself to track progress',
      ],
    };
  }

  // Check if AI service is available
  isAvailable(): boolean {
    return this.isInitialized && this.llm !== null;
  }
}

// Export singleton instance
export const aiCoachingService = new AICoachingService();
